import { createRoutine } from '@/models/models';

const routine = (id, name, blocks, countdown = 3) =>
  createRoutine({ id, name, blocks, countdown });

const block = (name, work, rest, rounds, color, exercises) => ({
  id: `${id_seed(name)}`,
  name,
  work,
  rest,
  rounds,
  color,
  ...(exercises ? { exercises } : {}),
});

function id_seed(s) {
  return `blk_${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

// ------- PRESETS (6+ complete) -------
export const PRESETS = [
  {
    id: 'preset_classic',
    name: 'Classic Tabata',
    level: 'Beginner',
    category: 'Beginner',
    calories: 180,
    accent: '#EF4444',
    blocks: [
      block('Tabata', 20, 10, 8, '#EF4444', [
        'Burpees',
        'Push-ups',
        'Air Squats',
        'Mountain Climbers',
        'Jumping Jacks',
        'Lunges',
        'Plank Jacks',
        'High Knees',
      ]),
    ],
  },
  {
    id: 'preset_light_hiit',
    name: 'Light HIIT 30/30',
    level: 'Beginner',
    category: 'Beginner',
    calories: 150,
    accent: '#22C55E',
    blocks: [
      block('Light HIIT', 30, 30, 8, '#22C55E', [
        'Squats',
        'Knee Push-ups',
        'Glute Bridge',
        'Bird Dog',
        'Standing March',
        'Wall Sit',
        'Toe Taps',
        'Calf Raises',
      ]),
    ],
  },
  {
    id: 'preset_hiit_30_15',
    name: 'HIIT 30/15',
    level: 'Intermediate',
    category: 'Intermediate',
    calories: 320,
    accent: '#38BDF8',
    blocks: [
      block('HIIT Blast', 30, 15, 10, '#38BDF8', [
        'Jump Squats',
        'Push-ups',
        'Skater Jumps',
        'Sit-ups',
        'Burpees',
        'High Knees',
        'Plank to Push-up',
        'Lunge Jumps',
        'Bicycle Crunch',
        'Tuck Jumps',
      ]),
    ],
  },
  {
    id: 'preset_rope_cardio',
    name: 'Rope Cardio 45/15',
    level: 'Intermediate',
    category: 'Intermediate',
    calories: 380,
    accent: '#F59E0B',
    blocks: [
      block('Jump Rope', 45, 15, 8, '#F59E0B', [
        'Basic Bounce',
        'Alternate Foot',
        'High Knees Rope',
        'Boxer Step',
        'Double Unders',
        'Criss Cross',
        'Side Swings',
        'Sprint Rope',
      ]),
    ],
  },
  {
    id: 'preset_tabata_x16',
    name: 'Tabata x16',
    level: 'Advanced',
    category: 'Advanced',
    calories: 460,
    accent: '#EF4444',
    blocks: [
      block('Tabata Round 1', 20, 10, 8, '#EF4444', [
        'Burpees',
        'Jump Squats',
        'Push-ups',
        'Mountain Climbers',
        'Tuck Jumps',
        'Plank Jacks',
        'Skaters',
        'High Knees',
      ]),
      block('Tabata Round 2', 20, 10, 8, '#F59E0B', [
        'Lunge Jumps',
        'Sit-ups',
        'Bear Crawl',
        'Squat Thrust',
        'V-ups',
        'Broad Jumps',
        'Russian Twist',
        'Sprint',
      ]),
    ],
  },
  {
    id: 'preset_plyo_blast',
    name: 'Plyometric Blast',
    level: 'Advanced',
    category: 'Advanced',
    calories: 520,
    accent: '#38BDF8',
    blocks: [
      block('Plyo Power', 40, 20, 10, '#38BDF8', [
        'Box Jumps',
        'Broad Jumps',
        'Tuck Jumps',
        'Lateral Bounds',
        'Depth Jumps',
        'Split Squat Jumps',
        'Clap Push-ups',
        'Burpee Broad Jump',
        'Single Leg Hops',
        'Squat Jumps',
      ]),
    ],
  },
  {
    id: 'preset_core_inferno',
    name: 'Core Inferno 45/15',
    level: 'Advanced',
    category: 'Advanced',
    calories: 340,
    accent: '#22C55E',
    blocks: [
      block('Core Inferno', 45, 15, 8, '#22C55E', [
        'Plank Hold',
        'Bicycle Crunch',
        'Leg Raises',
        'Russian Twist',
        'Hollow Hold',
        'Mountain Climbers',
        'Flutter Kicks',
        'Side Plank',
      ]),
    ],
  },
];

export const PRESETS_BY_LEVEL = {
  Beginner: PRESETS.filter((p) => p.level === 'Beginner'),
  Intermediate: PRESETS.filter((p) => p.level === 'Intermediate'),
  Advanced: PRESETS.filter((p) => p.level === 'Advanced'),
};

// ------- QUICK ACCESS (Home grid) -------
export const QUICK_ACCESS = [
  { id: 'preset_hiit_30_15', name: 'HIIT 30/15', tag: '10 rounds', accent: '#38BDF8' },
  { id: 'quick_emom', name: 'EMOM 10 min', tag: '10 min', accent: '#A78BFA' },
  { id: 'preset_rope_cardio', name: 'Jump Rope 45/15', tag: '8 rounds', accent: '#F59E0B' },
  { id: 'quick_trx', name: 'TRX Circuit', tag: '6 rounds', accent: '#22C55E' },
];

// EMOM + TRX quick presets not in the level list
export const EXTRA_PRESETS = [
  {
    id: 'quick_emom',
    name: 'EMOM 10 min',
    level: 'Intermediate',
    category: 'Intermediate',
    calories: 210,
    accent: '#A78BFA',
    blocks: [
      block('EMOM', 50, 10, 10, '#A78BFA', [
        '10 Burpees',
        '15 Push-ups',
        '20 Air Squats',
        '15 Sit-ups',
        '12 Jump Lunges',
        '20 Mountain Climbers',
        '10 Burpees',
        '30s Plank',
        '15 V-ups',
        '20 High Knees',
      ]),
    ],
  },
  {
    id: 'quick_trx',
    name: 'TRX Circuit',
    level: 'Intermediate',
    category: 'Intermediate',
    calories: 260,
    accent: '#22C55E',
    blocks: [
      block('TRX Circuit', 40, 20, 6, '#22C55E', [
        'TRX Row',
        'TRX Chest Press',
        'TRX Squat',
        'TRX Fallout',
        'TRX Hamstring Curl',
        'TRX Y-Fly',
      ]),
    ],
  },
];

// ------- RECENT ROUTINES (3, 4 exercises each) -------
export const RECENT_ROUTINES = [
  routine(
    'recent_full_body',
    'Full Body Burn',
    [
      block('Burpees', 20, 10, 4, '#EF4444'),
      block('Push-ups', 20, 10, 4, '#38BDF8'),
      block('Air Squats', 20, 10, 4, '#22C55E'),
      block('Plank', 30, 15, 4, '#F59E0B'),
    ]
  ),
  routine(
    'recent_cardio_crush',
    'Cardio Crush',
    [
      block('High Knees', 30, 15, 4, '#38BDF8'),
      block('Jumping Jacks', 30, 15, 4, '#22C55E'),
      block('Skaters', 30, 15, 4, '#F59E0B'),
      block('Sprint', 20, 20, 4, '#EF4444'),
    ]
  ),
  routine(
    'recent_core_focus',
    'Core Focus',
    [
      block('Crunches', 30, 15, 4, '#A78BFA'),
      block('Leg Raises', 30, 15, 4, '#38BDF8'),
      block('Russian Twist', 30, 15, 4, '#F59E0B'),
      block('Plank', 45, 15, 4, '#22C55E'),
    ]
  ),
];

// Classic tabata used by Home START button
export const CLASSIC_TABATA = {
  ...PRESETS[0],
  countdown: 3,
};

// Master lookup across everything playable
export const ALL_PLAYABLE = [
  ...PRESETS,
  ...EXTRA_PRESETS,
  ...RECENT_ROUTINES,
];

export const findRoutineById = (id, customRoutines = []) => {
  if (id === 'classic') return CLASSIC_TABATA;
  const custom = customRoutines.find((r) => r.id === id);
  if (custom) return custom;
  return ALL_PLAYABLE.find((r) => r.id === id) || null;
};

// ------- DEFAULT SETTINGS -------
export const DEFAULT_SETTINGS = {
  audio: {
    enabled: true,
    countdownVoice: false,
    volume: 0.8,
  },
  theme: 'dark',
  keepAwake: false,
  autoStart: true,
  countdownBefore: true,
  countdownSeconds: 3,
};
